import { Badge, Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CalendarLtr16Regular,
  CallInbound16Regular,
  CallOutbound16Regular,
  CheckmarkCircle16Filled,
  Circle16Regular,
  DismissCircle16Regular,
  Important16Filled,
  Location16Regular,
} from '@fluentui/react-icons';
import type { MultiTableItem, MultiTableViewDefinition, TableRecord } from '@powerportalspro/core';
import { RecordContext, useT } from '@powerportalspro/react';
import { Timeline } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { ACCOUNT_ID } from './DetailPageRecords';

// The featured account's tasks, phone calls and appointments, each drawn by a template of its own,
// beside its notes and messages, which keep the timeline's drawing. The view names the columns the
// templates show, so the timeline reads them with each row.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.TimelineShowcase.TimelineCustomRenderingMainDemo';

const COMPLETED = 1;
const CANCELED = 2;
const HIGH_PRIORITY = 2;

const useStyles = makeStyles({
  // A row drawn by its table's template: its title with an icon that says what state it is in, and
  // below it the badges and details that matter for its table.
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  line: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS,
  },
  detail: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
  },
  icon: {
    display: 'inline-flex',
    color: tokens.colorBrandForeground1,
  },
  completed: {
    color: tokens.colorPaletteGreenForeground1,
  },
  canceled: {
    color: tokens.colorNeutralForeground3,
  },
  done: {
    textDecorationLine: 'line-through',
    color: tokens.colorNeutralForeground2,
  },
});

// The raw value behind a column, and its text as the server formatted it.
function read(record: TableRecord | null | undefined, column: string): unknown {
  const cell = record?.properties?.[column];
  return cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
}

function formatted(record: TableRecord | null | undefined, column: string): string {
  return record?.formattedValues?.[column] ?? '';
}

const stateOf = (item: MultiTableItem) => read(item.record, 'statecode');

const stateColor = (item: MultiTableItem) =>
  stateOf(item) === COMPLETED ? 'success' : stateOf(item) === CANCELED ? 'subtle' : 'brand';

export function TimelineCustomRenderingMainSample() {
  const styles = useStyles();
  const t = useT();

  const stateClass = (item: MultiTableItem) =>
    mergeClasses(styles.icon, stateOf(item) === COMPLETED && styles.completed, stateOf(item) === CANCELED && styles.canceled);

  // The account's notes, messages and activities, each table with the columns its template shows:
  // the timeline reads them, beside its own, as it reads the view.
  const views = useMemo<MultiTableViewDefinition[]>(
    () => [
      {
        id: '7c1e5a9d-3f2b-4d6e-8a0c-9b4f2e6d1a01',
        displayName: t(`${STRINGS_BASE}.view-activities`),
        tables: [
          { table: 'annotation' },
          { table: 'ppp_portalcommunication' },
          {
            table: 'task',
            fetchXml: '<fetch><entity name="task"><attribute name="prioritycode" /><attribute name="statecode" /></entity></fetch>',
          },
          {
            table: 'phonecall',
            fetchXml:
              '<fetch><entity name="phonecall"><attribute name="directioncode" /><attribute name="phonenumber" /><attribute name="statecode" /></entity></fetch>',
          },
          {
            table: 'appointment',
            fetchXml: '<fetch><entity name="appointment"><attribute name="location" /></entity></fetch>',
          },
        ],
      },
    ],
    [t],
  );

  return (
    <RecordContext table="account" id={ACCOUNT_ID}>
      <Timeline
        pageSize={8}
        maxHeight="720px"
        customViewDefinitions={views}
        templates={{
          // A task: whether it is done, how urgent it is, and when it is due.
          task: (item) => (
            <div className={styles.root}>
              <div className={styles.line}>
                <span className={stateClass(item)}>
                  {stateOf(item) === COMPLETED ? (
                    <CheckmarkCircle16Filled />
                  ) : stateOf(item) === CANCELED ? (
                    <DismissCircle16Regular />
                  ) : (
                    <Circle16Regular />
                  )}
                </span>
                <Text weight="semibold" className={stateOf(item) === COMPLETED ? styles.done : undefined}>
                  {item.title}
                </Text>
              </div>
              <div className={styles.line}>
                <Badge appearance="tint" color={stateColor(item)}>
                  {formatted(item.record, 'statecode')}
                </Badge>
                {read(item.record, 'prioritycode') === HIGH_PRIORITY && (
                  <Badge appearance="filled" color="danger" icon={<Important16Filled />}>
                    {formatted(item.record, 'prioritycode')}
                  </Badge>
                )}
                <Text size={200}>{t(`${STRINGS_BASE}.due`, [item.startText ?? ''])}</Text>
              </div>
            </div>
          ),
          // A phone call: which way it went, to whom, and on what number.
          phonecall: (item) => {
            const outgoing = read(item.record, 'directioncode') === true;
            const number = read(item.record, 'phonenumber');
            return (
              <div className={styles.root}>
                <div className={styles.line}>
                  <span className={stateClass(item)}>{outgoing ? <CallOutbound16Regular /> : <CallInbound16Regular />}</span>
                  <Text weight="semibold">{item.title}</Text>
                </div>
                <div className={styles.line}>
                  <Badge appearance="tint" color={stateColor(item)}>
                    {formatted(item.record, 'statecode')}
                  </Badge>
                  <Text size={200}>
                    {t(`${STRINGS_BASE}.${outgoing ? 'call-to' : 'call-from'}`, [
                      (item.recipients ?? []).map((x) => x.name).join(', '),
                      typeof number === 'string' ? number : '',
                    ])}
                  </Text>
                </div>
              </div>
            );
          },
          // An appointment: when and where it takes place.
          appointment: (item) => {
            const location = read(item.record, 'location');
            return (
              <div className={styles.root}>
                <div className={styles.line}>
                  <span className={styles.icon}>
                    <CalendarLtr16Regular />
                  </span>
                  <Text weight="semibold">{item.title}</Text>
                </div>
                <div className={styles.line}>
                  <Text size={200}>{item.startText}</Text>
                  {typeof location === 'string' && location && (
                    <span className={styles.detail}>
                      <Location16Regular />
                      <Text size={200}>{location}</Text>
                    </span>
                  )}
                </div>
              </div>
            );
          },
        }}
      />
    </RecordContext>
  );
}
